/**
 * El deck que el usuario arma a mano desde la ficha ("Agregar al deck"), y
 * **dónde vive**: en un layout route sin path que envuelve a TODA la sección
 * Biblioteca (`/library` y sus vistas).
 *
 * Por qué no un `useState` en `LibraryScreen`: el deck moría con la pantalla,
 * así que el rodeo por la vieja pantalla Vocabulario (borrada en la card #348)
 * y la vuelta por el tab BIBLIOTECA lo vaciaban sin aviso. Y aunque hoy cruzar de `/library`
 * a `/library/:view` NO remonte —react-router renderiza las rutas sin `key`, así
 * que React reconcilia dos elementos idénticos en su lugar—, eso es un detalle
 * de reconciliación, no una garantía: alcanza con que un wrapper deje de ser
 * idéntico entre las dos rutas para que el deck se evapore otra vez.
 *
 * Tampoco es un módulo global: el layout se desmonta cuando NINGUNA ruta de la
 * sección matchea, así que el deck sigue siendo lo que dice ser —material
 * temporal de esta visita a la Biblioteca, sin backend ni storage— y salir de
 * la sección lo limpia. Ese es el único "quitar" que hay: adentro sólo se suma.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import type { LibWord } from './libData.js';

interface PracticeDeckValue {
  deck: readonly LibWord[];
  /** Suma una palabra (idempotente por `id`). */
  add: (w: LibWord) => void;
}

const PracticeDeckContext = createContext<PracticeDeckValue | null>(null);

export function usePracticeDeck(): PracticeDeckValue {
  const value = useContext(PracticeDeckContext);
  // Sin provider el deck sería un agujero negro silencioso (se agrega, nunca
  // aparece): mejor que reviente en el primer render.
  if (!value) throw new Error('usePracticeDeck fuera de <PracticeDeckProvider>');
  return value;
}

export function PracticeDeckProvider({ children }: { children: ReactNode }) {
  const [deck, setDeck] = useState<readonly LibWord[]>([]);
  const add = useCallback((w: LibWord) => {
    setDeck((prev) => (prev.some((x) => x.id === w.id) ? prev : [...prev, w]));
  }, []);
  const value = useMemo<PracticeDeckValue>(() => ({ deck, add }), [deck, add]);
  return <PracticeDeckContext.Provider value={value}>{children}</PracticeDeckContext.Provider>;
}

/** Layout route de la sección (ver `App.tsx`): provider + `<Outlet />`. */
export function PracticeDeckSection() {
  return (
    <PracticeDeckProvider>
      <Outlet />
    </PracticeDeckProvider>
  );
}
